import { EmployerOrWorkplace } from "../types";

export interface SupportedInsurance {
  primaryPayerId: string;
  displayName: string;
}
export const aetnaInsurance = {
  primaryPayerId: "60054",
  displayName: "Aetna",
} as SupportedInsurance;

export const medicareInsurance = {
  primaryPayerId: "CMS",
  displayName: "Medicare Part B",
} as SupportedInsurance;

export const humanaInsurance = {
  primaryPayerId: "61101",
  displayName: "Humana",
} as SupportedInsurance;

export const premeraWashingtonInsurance = {
  primaryPayerId: "00430",
  displayName: "Premera Blue Cross",
} as SupportedInsurance;

export const cignaInsurance = {
  primaryPayerId: "62308",
  displayName: "Cigna",
} as SupportedInsurance;

export const anthemBCBSInsurance = {
  primaryPayerId: "00060",
  displayName: "Anthem BCBS",
} as SupportedInsurance;

export const otherInsurance = {
  primaryPayerId: "other",
  displayName: "Other",
} as SupportedInsurance;

export const AMAZON_SUPPORTED_INSURANCE_PLAN_GROUPS = {
  [aetnaInsurance.primaryPayerId]: ["868402", "868403", "868404"],
  [cignaInsurance.primaryPayerId]: ["3345145"],
  [premeraWashingtonInsurance.primaryPayerId]: ["4000083"],
};

export const getInsurances = (): SupportedInsurance[] => {
  const defaultOptions = [
    aetnaInsurance,
    anthemBCBSInsurance,
    medicareInsurance,
    humanaInsurance,
    premeraWashingtonInsurance,
    cignaInsurance,
  ];

  return [
    ...defaultOptions.sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    ),
    otherInsurance,
  ];
};

export const getInsurancesOptions = (): {
  label: string;
  value: string;
}[] => {
  return getInsurances().map((insurance) => ({
    label: insurance.displayName,
    value: insurance.primaryPayerId,
  }));
};

const PAYER_ID_TO_DISPLAY_NAME: Record<string, string> = [
  aetnaInsurance,
  medicareInsurance,
  humanaInsurance,
  premeraWashingtonInsurance,
  cignaInsurance,
  anthemBCBSInsurance,
  otherInsurance,
].reduce<Record<string, string>>((acc, insurance) => {
  acc[insurance.primaryPayerId] = insurance.displayName;
  return acc;
}, {});

/**
 * Resolves a payer's display name from its payer ID. Canvas Coverage stores the
 * payer as an Organization reference (payer ID only), so reads from Canvas use
 * this to recover a human-readable name. Returns undefined for unknown payers.
 */
export const getPayerName = (
  payerId?: string | null
): string | undefined =>
  payerId ? PAYER_ID_TO_DISPLAY_NAME[payerId] : undefined;

/**
 * Ordered keyword→payer aliases for fuzzy matching HubSpot payer strings that
 * don't exactly equal a supported display name. The first rule whose keyword
 * appears in the (lowercased) payer string wins, so put more specific keywords
 * first. Extend this list as new HubSpot payer spellings show up.
 *
 * NOTE: the Anthem rule intentionally also maps non-Anthem BCBS plans (e.g.
 * "BCBS of Michigan") to Anthem, since Anthem is the only BCBS payer RMH
 * supports — an accepted tradeoff so the common cases prefill. The admin can
 * still override.
 */
const PAYER_ALIAS_RULES: { keywords: string[]; insurance: SupportedInsurance }[] =
  [
    // Premera — checked BEFORE the broad Anthem/BCBS rule so a Premera Blue
    // Cross string is never swallowed as Anthem. Canvas stores it as
    // "WA/AK Blue Cross - Premera", which extractPayerName trims to
    // "WA/AK Blue Cross" before this runs, so "wa/ak" (which also covers
    // "WA/AK BCBS") is the token that matches the real data.
    { keywords: ["premera", "wa/ak"], insurance: premeraWashingtonInsurance },
    // "CT BCBS", "Anthem", "Blue Cross Blue Shield of ...", etc.
    {
      keywords: ["bcbs", "blue cross blue shield", "anthem"],
      insurance: anthemBCBSInsurance,
    },
  ];

/**
 * Resolves a supported-insurance autocomplete option ({ label, value }) from a
 * payer display name (e.g. HubSpot's `payer` property, "Aetna"). Matching is
 * case-insensitive and whitespace-tolerant. An exact display-name match wins;
 * otherwise the payer-alias rules above are applied. Returns undefined for
 * unknown or "Other" payers so the caller leaves the field for the admin to
 * pick, rather than forcing a value that would change eligibility handling.
 */
export const getInsuranceOptionByDisplayName = (
  displayName?: string | null
): { label: string; value: string } | undefined => {
  const target = displayName?.trim().toLowerCase();
  if (!target) return undefined;

  // Exact match first, so specific payers win before the alias rules below.
  const match = getInsurances().find(
    (insurance) =>
      insurance.primaryPayerId !== otherInsurance.primaryPayerId &&
      insurance.displayName.toLowerCase() === target
  );

  if (match) {
    return { label: match.displayName, value: match.primaryPayerId };
  }

  const alias = PAYER_ALIAS_RULES.find((rule) =>
    rule.keywords.some((keyword) => target.includes(keyword))
  );

  return alias
    ? { label: alias.insurance.displayName, value: alias.insurance.primaryPayerId }
    : undefined;
};

export const isSelfPay = (
  employerOrWorkplace?: string | null,
  payerId?: string | null
): boolean => {
  return (
    employerOrWorkplace === EmployerOrWorkplace.Amazon &&
    payerId === otherInsurance.primaryPayerId
  );
};

export const checkIsAmazonInsurancePlan = (payerId: string) => {
  //check if the payor id is supported for amazon
  if (!AMAZON_SUPPORTED_INSURANCE_PLAN_GROUPS[payerId]) {
    return false;
  }

  return true;
};

/**
 * Insurance is supported when the employer is Amazon or the payer id is not other (self-pay).
 * Employer "Other" is never supported — always off-ramped.
 */
export const checkIsSupportedInsurance = ({
  employerOrWorkplace,
  payerId,
}: {
  employerOrWorkplace?: string | null;
  payerId?: string | null;
}): boolean => {
  if (employerOrWorkplace === EmployerOrWorkplace.Other) {
    return false;
  }

  return (
    employerOrWorkplace === EmployerOrWorkplace.Amazon ||
    payerId !== otherInsurance.primaryPayerId
  );
};
